import { lazy } from 'react';

// ===============================
// LAZY + AUTO-RECOVERY ANTE CHUNK ROTO
// ===============================
// Sintoma tipico en mobile despues de un deploy:
//   "TypeError: 'text/html' is not a valid JavaScript MIME type."
// Causa:
//   El SW (workbox) sirvio un index.html viejo que referencia chunks con hash
//   viejo. Al hacer import() dinamico, Netlify devuelve 404 con body HTML
//   (la 404 page por default) y el navegador rechaza el modulo.
//
// Estrategia:
//   1) Primer intento: dejamos pasar el error. Si es transitorio
//      (mala red), un retry simple alcanza.
//   2) Si vemos un error que matchea "chunk roto" (MIME, ChunkLoadError,
//      Failed to fetch dynamically imported module, Importing a module
//      script failed), forzamos un hard reload del index.html para
//      bajar el bundle nuevo y los hashes correctos.
//   3) Guard contra loop: usamos sessionStorage para no reintentar
//      mas de una vez por sesion. Si despues del reload el chunk
//      sigue roto, propagamos el error al ErrorBoundary normal.
// ===============================

const RELOAD_FLAG = 'impulza_chunk_reload_v1';

// Ventana de enfriamiento entre intentos de recuperacion.
//
// POR QUE NO ALCANZA CON "UNA VEZ POR SESION"
// El guard original marcaba el flag y no lo borraba nunca, asi que la pestaña
// tenia UN solo intento de por vida. Con dos deploys el mismo dia —lo normal
// cuando se corrige algo y despues se revierte— el primero consumia el intento
// y a partir de ahi la app detectaba el chunk roto y se quedaba mirando: el
// usuario veia pantallas en blanco, sin error, hasta cerrar la pestaña.
//
// POR QUE NO SE LIMPIA AL ARRANCAR BIEN
// Seria lo obvio y genera un loop infinito: arranca ok, se limpia el flag, se
// navega a una ruta lazy rota, recarga, arranca ok, se limpia... El cooldown
// corta las dos puntas: un incidente nuevo separado en el tiempo tiene su
// intento, pero dos fallas seguidas no encadenan recargas.
const RETRY_COOLDOWN_MS = 10 * 60 * 1000; // 10 min

function looksLikeChunkLoadError(error) {
  if (!error) return false;
  const msg = String(error.message || error || '');
  return (
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /'text\/html'.*is not a valid JavaScript MIME type/i.test(msg) ||
    /Expected a JavaScript(?:-or-Wasm)? module script/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

function alreadyTriedReload() {
  try {
    const raw = window.sessionStorage && window.sessionStorage.getItem(RELOAD_FLAG);
    if (!raw) return false;
    const ultimoIntento = Number(raw);
    // Flag viejo sin timestamp parseable: lo tratamos como intento vigente para
    // no arriesgar un loop.
    if (!Number.isFinite(ultimoIntento)) return true;
    return (Date.now() - ultimoIntento) < RETRY_COOLDOWN_MS;
  } catch (e) {
    return false;
  }
}

function markReloadAttempted() {
  try {
    window.sessionStorage && window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch (e) { /* retry best-effort */ }
}

export function clearChunkReloadFlag() {
  try {
    window.sessionStorage && window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch (e) { /* retry best-effort */ }
}

async function hardReload() {
  // Nukear caches + SW antes de reload. Asi el index.html nuevo no vuelve a
  // ser servido del precache viejo del SW.
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch (e) { /* retry best-effort */ }
  try {
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch (e) { /* retry best-effort */ }
  try {
    window.location.reload();
  } catch (e) { /* retry best-effort */ }
}

export function lazyWithRetry(factory) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (looksLikeChunkLoadError(error) && !alreadyTriedReload()) {
        markReloadAttempted();
        // No esperamos a que termine: el reload mata la pagina igual.
        hardReload();
        // Devolvemos una "promesa eterna" para que Suspense siga mostrando
        // el fallback (spinner) hasta que el reload se ejecute. Si propagaramos
        // el error, el ErrorBoundary mostraria "Algo salio mal" justo antes
        // del reload, lo cual es feo.
        return new Promise(() => {});
      }
      throw error;
    }
  });
}

export { looksLikeChunkLoadError };
